import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LoginModal from '../LoginModal/LoginModal';
import useSwipeToClose from '../../hooks/useSwipeToClose';
import backendClient from '../../services/backendClient';
import { useUser } from '../../contexts/UserContext';
import { useStripePrices } from '../../services/priceConfig';
import { clearInProgressMealPlan } from '../../utils/inProgressMealPlan';

// Main User Area Drawer Component
const UserAreaDrawer = ({ isOpen, onClose, onShowMyMeals }) => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const location = useLocation();
  const prices = useStripePrices();
  const { numberOfPeople, budget, selectedCuisine, dietaryPreferences, cookingStyle } = useUser();
  const [token, setToken] = useState(localStorage.getItem('authToken'));
  const [user, setUser] = useState({ name: '', email: '', phone: '' });
  const [isVisible, setIsVisible] = useState(false);
  const [hasSavedMealPlan, setHasSavedMealPlan] = useState(false);

  useEffect(() => {
    if (isOpen) {
      requestAnimationFrame(() => setIsVisible(true));
    } else {
      setIsVisible(false);
    }
  }, [isOpen]);

  useEffect(() => {
    // Re-check token from localStorage in case it was cleared by another component
    const currentToken = localStorage.getItem('authToken');
    if (currentToken !== token) {
      setToken(currentToken);
    }
    
    if (currentToken) {
      // Fetch user data
      backendClient.get('/api/user/me')
        .then(data => {
          if (data && data.email) setUser(data);
        })
        .catch(err => {
          if (err.name === 'AuthError') {
            console.log('UserAreaDrawer: Token expired, clearing');
            localStorage.removeItem('authToken');
            setToken(null);
            return;
          }
          console.error('Failed to fetch user:', err);
        });

      // Check if user has a saved meal plan
      backendClient.get('/api/mealplan')
        .then(data => {
          if (data && data.mealPlan && data.mealPlan.length > 0 && data.isSaved) {
            setHasSavedMealPlan(true);
          } else {
            setHasSavedMealPlan(false);
          }
        })
        .catch(err => {
          console.error('Failed to check meal plan:', err);
        });
    }
  }, [token, isOpen]);

  const handleMyMealsClick = () => {
    handleClose();
    setTimeout(() => {
      if (hasSavedMealPlan) {
        navigate('/mymeals');
      } else {
        navigate('/wizard');
      }
    }, 300);
  };

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => onClose(), 250);
  };

  const { containerRef, dragOffset, shouldRender, isVisible: swipeVisible, DragHandle } = useSwipeToClose(handleClose, isVisible);

  const handleLoginSuccess = (newToken) => {
    setToken(newToken);
    // Close will be handled by LoginModal
  };

  const handleLogout = () => {
    // Persist current preferences to localStorage before logout
    // so they survive into the anonymous session
    try {
      localStorage.setItem('9meals_preferences', JSON.stringify({
        numberOfPeople,
        budget,
        selectedCuisine,
        dietaryPreferences,
        cookingStyle
      }));
    } catch (e) {
      console.warn('[Logout] Failed to persist preferences:', e);
    }

    localStorage.removeItem('authToken');
    localStorage.removeItem('email');
    clearInProgressMealPlan();
    setToken(null);
    handleClose();
    // Navigate to landing page after logout
    window.location.href = '/';
  };

  if (!isOpen && !shouldRender) return null;

  // If not logged in, show login modal instead
  if (!token) {
    return (
      <LoginModal
        isOpen={isOpen}
        onClose={onClose}
        onLoginSuccess={handleLoginSuccess}
        context="signin"
      />
    );
  }

  return createPortal(
    <>
      <div 
        className="fixed flex items-end justify-center inset-0 z-[200] drawer-overlay"
        style={{
          backgroundColor: `rgba(13, 12, 7, ${swipeVisible ? 0.5 : 0})`,
          transition: 'background-color 250ms cubic-bezier(0.25, 0.46, 0.45, 0.94)'
        }}
        onClick={handleClose}
      >
        <div 
          ref={containerRef}
          className="flex flex-col w-full bg-[#F8F8F7] rounded-[12px_12px_0px_0px] max-w-5xl mx-auto shadow-lg drawer-content overflow-hidden"
          style={{
            height: 'auto',
            transform: swipeVisible ? `translateY(${Math.max(0, dragOffset)}px)` : 'translateY(100%)',
            transition: dragOffset === 0 ? 'transform 250ms cubic-bezier(0.25, 0.46, 0.45, 0.94)' : 'none'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <DragHandle />
          
          {/* Header */}
          <div className="flex-shrink-0 pt-8 pb-0 px-6">
            <button 
              onClick={handleClose}
              className="absolute top-1 right-1 flex justify-center items-center md:hover:bg-gray-200 w-[36px] h-[36px] rounded-[12px] transition-colors z-10"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M16.381 6.38079C16.7227 6.03908 17.2776 6.03908 17.6193 6.38079C17.961 6.7225 17.961 7.27737 17.6193 7.61907L13.2385 11.9999L17.6193 16.3808L17.6789 16.4472C17.9595 16.7909 17.9398 17.2986 17.6193 17.6191C17.2989 17.9395 16.7911 17.9592 16.4474 17.6786L16.381 17.6191L12.0002 13.2382L7.61932 17.6191C7.27761 17.9608 6.72275 17.9608 6.38104 17.6191C6.03933 17.2774 6.03933 16.7225 6.38104 16.3808L10.7619 11.9999L6.38104 7.61907L6.32147 7.55267C6.04089 7.20899 6.06059 6.70124 6.38104 6.38079C6.70149 6.06034 7.20923 6.04064 7.55291 6.32122L7.61932 6.38079L12.0002 10.7617L16.381 6.38079Z" fill="#56554D"/>
              </svg>
            </button>
            
            <h2 className="text-[var(--Color-Grays-Gray-10,#0D0C07)] text-[18px] font-bold leading-[32px] mb-4">
              {t('profile.hello')}{user.name ?`, ${user.name}` : ''}
            </h2>
          </div>
          
          {/* Content */}
          <div className="flex-1 overflow-y-auto min-h-0 px-6 pb-8">
            <div className="box-border content-stretch flex flex-col gap-[12px] items-start pb-[64px] pt-0 px-0 relative w-full">
              
              {/* My Meals Option */}
              <button
                onClick={handleMyMealsClick}
                className="bg-white min-h-[48px] relative rounded-[12px] shrink-0 w-full text-left md:hover:bg-[#F8F8F7] transition-colors"
              >
                <div aria-hidden="true" className="absolute border-[#56554d] border-[0.5px] border-solid inset-0 pointer-events-none rounded-[12px]"></div>
                <div className="min-h-inherit size-full">
                  <div className="box-border content-stretch flex items-start min-h-inherit p-[12px] relative w-full">
                    {/* Icon */}
                    <div className="box-border content-stretch flex items-center pl-0 pr-[8px] py-0 relative shrink-0">
                      <div className="relative shrink-0 size-[24px]">
                        <div className="absolute left-1/2 size-[20px] top-1/2 translate-x-[-50%] translate-y-[-50%]">
                          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
  <path d="M5.83333 17.5H14.1667M16.25 10L18.3333 5M13.5417 2.5C13.7667 2.58333 14.2083 2.94167 14.1667 3.63333C14.1167 4.325 13.3917 4.63333 13.3333 5.31667C13.2917 5.96667 13.6167 6.35 13.9417 6.66667M9.375 2.5C9.6 2.58333 10.0417 2.94167 9.99167 3.63333C9.95 4.325 9.21667 4.63333 9.175 5.31667C9.125 5.96667 9.45 6.35 9.775 6.66667M5.20833 2.5C5.43333 2.58333 5.875 2.94167 5.83333 3.63333C5.78333 4.325 5.05833 4.63333 5 5.31667C4.95833 5.96667 5.28333 6.35 5.61667 6.66667M10 17.5C11.9891 17.5 13.8968 16.7098 15.3033 15.3033C16.7098 13.8968 17.5 11.9891 17.5 10H2.5C2.5 11.9891 3.29018 13.8968 4.6967 15.3033C6.10322 16.7098 8.01088 17.5 10 17.5Z" stroke="#0D0C07" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/>
</svg>
                        </div>
                      </div>
                    </div>
                    {/* Content */}
                    <div className="basis-0 content-stretch flex flex-col gap-[2px] grow items-start min-h-px min-w-px relative shrink-0">
                      <div className="content-stretch flex gap-[4px] items-start relative shrink-0">
                        <div className="content-stretch flex gap-[10px] items-start relative shrink-0">
                          <p className="font-semibold leading-[24px] relative shrink-0 text-[#0d0c07] text-[14px] whitespace-nowrap">{hasSavedMealPlan ? t('profile.myMeals') : t('profile.createMyMeals')}</p>
                        </div>
                      </div>
                    </div>
                    {/* More Arrow */}
                    <div className="box-border content-stretch flex items-center pl-[8px] pr-0 py-0 relative self-stretch shrink-0 w-[32px]">
                      <div className="absolute right-0 size-[24px] top-1/2 translate-y-[-50%]">
                        <div className="absolute left-1/2 size-[20px] top-1/2 translate-x-[-50%] translate-y-[-50%]">
                          <svg className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 20 20">
                            <path d="M4.167 10h11.666M10 4.167L15.833 10 10 15.833" stroke="#0D0C07" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </button>

              {/* Favorite Meals Option */}
              <button
                onClick={() => { handleClose(); setTimeout(() => navigate('/favorites'), 300); }}
                className="bg-white min-h-[48px] relative rounded-[12px] shrink-0 w-full text-left md:hover:bg-[#F8F8F7] transition-colors"
              >
                <div aria-hidden="true" className="absolute border-[#56554d] border-[0.5px] border-solid inset-0 pointer-events-none rounded-[12px]"></div>
                <div className="min-h-inherit size-full">
                  <div className="box-border content-stretch flex items-start min-h-inherit p-[12px] relative w-full">
                    {/* Icon */}
                    <div className="box-border content-stretch flex items-center pl-0 pr-[8px] py-0 relative shrink-0">
                      <div className="relative shrink-0 size-[24px]">
                        <div className="absolute left-1/2 size-[20px] top-1/2 translate-x-[-50%] translate-y-[-50%]">
                          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
  <path d="M2.08301 7.91667C2.08303 6.98934 2.36434 6.08382 2.88979 5.31972C3.41524 4.55562 4.1601 3.96888 5.02601 3.637C5.89192 3.30512 6.83813 3.24371 7.73968 3.46087C8.64123 3.67804 9.45569 4.16357 10.0755 4.85334C10.1192 4.90001 10.172 4.93724 10.2306 4.96268C10.2892 4.98811 10.3524 5.00124 10.4163 5.00124C10.4802 5.00124 10.5435 4.98811 10.6021 4.96268C10.6608 4.93724 10.7135 4.90001 10.7572 4.85334C11.375 4.15909 12.1897 3.66948 13.0927 3.44967C13.9957 3.22987 14.9442 3.29029 15.8121 3.62291C16.6799 3.95552 17.4258 4.54455 17.9506 5.31159C18.4754 6.07863 18.7541 6.98731 18.7497 7.91667C18.7497 9.825 17.4997 11.25 16.2497 12.5L11.673 16.9275C11.5177 17.1058 11.3263 17.249 11.1114 17.3478C10.8965 17.4465 10.663 17.4982 10.4266 17.4998C10.1901 17.5012 9.95604 17.4524 9.73991 17.3564C9.52378 17.2605 9.33053 17.1197 9.17301 16.9433L4.58301 12.5C3.33301 11.25 2.08301 9.83334 2.08301 7.91667Z" stroke="#0D0C07" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
</svg>
                        </div>
                      </div>
                    </div>
                    {/* Content */}
                    <div className="basis-0 content-stretch flex flex-col gap-[2px] grow items-start min-h-px min-w-px relative shrink-0">
                      <div className="content-stretch flex gap-[4px] items-start relative shrink-0">
                        <div className="content-stretch flex gap-[10px] items-start relative shrink-0">
                          <p className="font-semibold leading-[24px] relative shrink-0 text-[#0d0c07] text-[14px] whitespace-nowrap">{t('profileSettings.favoriteMeals')}</p>
                        </div>
                      </div>
                    </div>
                    {/* More Arrow */}
                    <div className="box-border content-stretch flex items-center pl-[8px] pr-0 py-0 relative self-stretch shrink-0 w-[32px]">
                      <div className="absolute right-0 size-[24px] top-1/2 translate-y-[-50%]">
                        <div className="absolute left-1/2 size-[20px] top-1/2 translate-x-[-50%] translate-y-[-50%]">
                          <svg className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 20 20">
                            <path d="M4.167 10h11.666M10 4.167L15.833 10 10 15.833" stroke="#0D0C07" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </button>

              {/* My Profile Option */}
              <button
                onClick={() => {
                  handleClose();
                  setTimeout(() => {
                    const returnTo = encodeURIComponent(`${location.pathname}${location.search || ''}`);
                    navigate(`/myprofile?returnTo=${returnTo}`);
                  }, 300);
                }}
                className="bg-white min-h-[48px] relative rounded-[12px] shrink-0 w-full text-left md:hover:bg-[#F8F8F7] transition-colors"
              >
                <div aria-hidden="true" className="absolute border-[#56554d] border-[0.5px] border-solid inset-0 pointer-events-none rounded-[12px]"></div>
                <div className="min-h-inherit size-full">
                  <div className="box-border content-stretch flex items-start min-h-inherit p-[12px] relative w-full">
                    {/* Icon */}
                    <div className="box-border content-stretch flex items-center pl-0 pr-[8px] py-0 relative shrink-0">
                      <div className="relative shrink-0 size-[24px]">
                        <div className="absolute left-1/2 size-[20px] top-1/2 translate-x-[-50%] translate-y-[-50%]">
                          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
  <path d="M9.99967 10.8333C12.3009 10.8333 14.1663 8.96785 14.1663 6.66667C14.1663 4.36548 12.3009 2.5 9.99967 2.5C7.69849 2.5 5.83301 4.36548 5.83301 6.66667C5.83301 8.96785 7.69849 10.8333 9.99967 10.8333ZM9.99967 10.8333C11.7678 10.8333 13.4635 11.5357 14.7137 12.786C15.964 14.0362 16.6663 15.7319 16.6663 17.5M9.99967 10.8333C8.23156 10.8333 6.53587 11.5357 5.28563 12.786C4.03539 14.0362 3.33301 15.7319 3.33301 17.5" stroke="#0D0C07" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/>
</svg>
                        </div>
                      </div>
                    </div>
                    {/* Content */}
                    <div className="basis-0 content-stretch flex flex-col gap-[2px] grow items-start min-h-px min-w-px relative shrink-0">
                      <div className="content-stretch flex gap-[4px] items-start relative shrink-0">
                        <div className="content-stretch flex gap-[10px] items-start relative shrink-0">
                          <p className="font-semibold leading-[24px] relative shrink-0 text-[#0d0c07] text-[14px] whitespace-nowrap">{t('profile.myProfile')}</p>
                        </div>
                      </div>
                    </div>
                    {/* More Arrow */}
                    <div className="box-border content-stretch flex items-center pl-[8px] pr-0 py-0 relative self-stretch shrink-0 w-[32px]">
                      <div className="absolute right-0 size-[24px] top-1/2 translate-y-[-50%]">
                        <div className="absolute left-1/2 size-[20px] top-1/2 translate-x-[-50%] translate-y-[-50%]">
                          <svg className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 20 20">
                            <path d="M4.167 10h11.666M10 4.167L15.833 10 10 15.833" stroke="#0D0C07" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </button>

              {/* Premium Section - only show upsell for free users */}
              {!user.isPremium && (
              <div className="bg-[#fff] relative rounded-[12px] border-[0.5px] border-[#e2e1df] shrink-0 w-full">
                <div className="flex gap-3 items-start px-4 pt-4 pb-5">
                  {/* Icon */}
                  <div className="flex items-center justify-center h-7 py-1 shrink-0">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <g clipPath="url(#clip0_uad_premium)">
                        <path d="M16.6667 2.5V5.83333M18.3334 4.16667H15M3.33336 14.1667V15.8333M4.1667 15H2.50003M8.28086 12.9167C8.20647 12.6283 8.05615 12.3651 7.84555 12.1545C7.63494 11.9439 7.37176 11.7936 7.08336 11.7192L1.97086 10.4008C1.88364 10.3761 1.80687 10.3235 1.75221 10.2512C1.69754 10.1789 1.66797 10.0907 1.66797 10C1.66797 9.90933 1.69754 9.82113 1.75221 9.7488C1.80687 9.67646 1.88364 9.62392 1.97086 9.59917L7.08336 8.28C7.37166 8.20567 7.63477 8.05548 7.84537 7.84503C8.05596 7.63459 8.20634 7.37157 8.28086 7.08333L9.5992 1.97083C9.6237 1.88326 9.67618 1.80612 9.74863 1.75116C9.82108 1.69621 9.90951 1.66646 10.0004 1.66646C10.0914 1.66646 10.1798 1.69621 10.2523 1.75116C10.3247 1.80612 10.3772 1.88326 10.4017 1.97083L11.7192 7.08333C11.7936 7.37173 11.9439 7.63491 12.1545 7.84551C12.3651 8.05612 12.6283 8.20643 12.9167 8.28083L18.0292 9.59833C18.1171 9.62258 18.1946 9.67501 18.2499 9.74756C18.3052 9.82012 18.3351 9.9088 18.3351 10C18.3351 10.0912 18.3052 10.1799 18.2499 10.2524C18.1946 10.325 18.1171 10.3774 18.0292 10.4017L12.9167 11.7192C12.6283 11.7936 12.3651 11.9439 12.1545 12.1545C11.9439 12.3651 11.7936 12.6283 11.7192 12.9167L10.4009 18.0292C10.3764 18.1167 10.3239 18.1939 10.2514 18.2488C10.179 18.3038 10.0905 18.3335 9.99961 18.3335C9.90868 18.3335 9.82025 18.3038 9.7478 18.2488C9.67535 18.1939 9.62287 18.1167 9.59836 18.0292L8.28086 12.9167Z" stroke="#0D0C07" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/>
                      </g>
                      <defs>
                        <clipPath id="clip0_uad_premium">
                          <rect width="20" height="20" fill="white" />
                        </clipPath>
                      </defs>
                    </svg>
                  </div>
                  {/* Content */}
                  <div className="flex flex-col flex-grow">
                    <div className="flex flex-col gap-1">
                      <p className="text-[#0d0c07] text-[15.2px] leading-[28px] font-bold">
                        {t('premium.becomePremium')}
                      </p>
                      <p className="text-[#006600] text-[14px] leading-[24px]">
                        {t('premium.onlyPrice', { price: prices.yearlyPerMonth })}
                      </p>
                    </div>
                    <Link
                      to="/premium"
                      className="text-[#0d0c07] font-semibold md:hover:border-[#0d0c07] text-[14px] leading-[24px] border-b border-[#ffcb00] inline-block self-start"
                      onClick={handleClose}
                    >
                      {t('premium.discoverFeatures')}
                    </Link>
                  </div>
                </div>
              </div>
              )}

              {/* Logout Option */}
              <button
                onClick={handleLogout}
                className="bg-white min-h-[48px] relative rounded-[12px] shrink-0 w-full text-left md:hover:bg-[#F8F8F7] transition-colors"
              >
                <div aria-hidden="true" className="absolute border-[#56554d] border-[0.5px] border-solid inset-0 pointer-events-none rounded-[12px]"></div>
                <div className="min-h-inherit size-full">
                  <div className="box-border content-stretch flex items-start min-h-inherit p-[12px] relative w-full">
                    {/* Icon */}
                    <div className="box-border content-stretch flex items-center pl-0 pr-[8px] py-0 relative shrink-0">
                      <div className="relative shrink-0 size-[24px]">
                        <div className="absolute left-1/2 size-[20px] top-1/2 translate-x-[-50%] translate-y-[-50%]">
                          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none">
  <path d="M9 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H9M16 17L21 12M21 12L16 7M21 12H9" stroke="black" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/>
</svg>
                        </div>
                      </div>
                    </div>
                    {/* Content */}
                    <div className="basis-0 content-stretch flex flex-col gap-[2px] grow items-start min-h-px min-w-px relative shrink-0">
                      <div className="content-stretch flex gap-[4px] items-start relative shrink-0">
                        <div className="content-stretch flex gap-[10px] items-start relative shrink-0">
                          <p className="font-semibold leading-[24px] relative shrink-0 text-[#0d0c07] text-[14px] whitespace-nowrap">{t('profile.logout')}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </button>

            </div>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
};

export default UserAreaDrawer;
